import { useCallback } from "react"

import { useEntityAI } from "@/api/useInventory"
import { Flex, Skeleton, Space, Text, Tooltip } from "@/atoms"
import { capitalizeFirstLetter } from "@/helpers/string.helpers"
import { useTranslation } from "@/hooks/useTranslation"
import { AiAdr, NodeIndexOutlined, RobotOutlined, Runtime } from "@/icons"
import { CopyLinkButton } from "@/molecules/CopyLinkButton"
import { ShowMore } from "@/molecules/ShowMore"
import { TitleDrawer } from "@/molecules/TitleDrawer"
import { useLink } from "@/organisms/Drawers/hooks/useLink"
import { getAgentsAi } from "@/pages/DiscoveryPage/application/ai-discovery/useAiDiscovery.select"

import styles from "./TitleAgentAiDiscoveryDrawer.module.scss"

import type { AgentAiResponse } from "@/api/useInventory.types"
import type { TitleAgentAiDiscoveryDrawerProps } from "."

export const TitleAgentAiDiscoveryDrawer = (
  props: TitleAgentAiDiscoveryDrawerProps
) => {
  const { drawerId, record } = props
  const link = useLink(drawerId)
  const { t } = useTranslation()
  const select = useCallback(
    (response: AgentAiResponse) => getAgentsAi({ t })(response),
    [t]
  )
  const ctx = useEntityAI(record?.id, "agent", undefined, { select })
  const { response, isLoading } = ctx
  const { data } = response || {}
  const { name, isGeneratedName, insights, apps } = data || {}
  const { runtime, isRoot } = insights || {}

  if (isLoading)
    return (
      <Flex gap={8} className={styles.skeletonWrapper}>
        <Skeleton.Avatar active shape="square" />
        <Skeleton.Input active />
      </Flex>
    )

  return (
    <TitleDrawer
      loading={isLoading}
      icon={<RobotOutlined />}
      title={
        <Flex gap={4} align="center">
          {name}
          {isGeneratedName && (
            <Tooltip title={t("inventory.ai.generatedName")}>
              <span className={styles.iconAi}>
                <AiAdr />
              </span>
            </Tooltip>
          )}
        </Flex>
      }
      subTitle={
        <Space
          separator={
            <Text type="secondary" className="n-4-color-text-divider-ks">
              |
            </Text>
          }
        >
          {!!apps?.items.length && (
            <Text type="secondary" className="sm-text-ks">
              {`${capitalizeFirstLetter(t("general.application", { count: apps.items.length }))}: ${apps.label}`}
              {apps.count > 0 && (
                <ShowMore.Popover {...apps}>
                  <span>{` +${apps.count}`}</span>
                </ShowMore.Popover>
              )}
            </Text>
          )}
          {runtime && (
            <Flex align="center" gap={4}>
              <Runtime className={styles.icon} />
              <Text type="secondary" className="sm-text-ks">
                Running
              </Text>
            </Flex>
          )}
          {isRoot && (
            <Flex align="center" gap={4}>
              <NodeIndexOutlined className={styles.icon} />
              <Text type="secondary" className="sm-text-ks">
                Root
              </Text>
            </Flex>
          )}
        </Space>
      }
      aside={<CopyLinkButton link={link} />}
    />
  )
}
